import React, { useContext, useState, useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
} from "react-native";
import Swipeable from "../../../../lib/react-native-swipeable";

import AppTheme from "../../AppTheme";
import ImageComponent from "../../common/ImageComponent";
import ReflectionContext from "../ReflectionContext";
import ReflectionHours from "./ReflectionHours";
import {
  getGroupImageTag,
  hasItems,
  isBaseCoreElement,
} from "../../../../helpers/utils";
import ReflectionItemChart from "../chart/ReflectionItemChart";
import CollapseIcon from "../../common/icons/CollapseIcon";
import RoundCheckbox from "../../common/RoundCheckbox";
import EditedHoursIcon from "../../common/icons/EditedHoursIcon";
import LockIcon from "../../common/icons/LockIcon";

const ReflectionItemHeader = ({
  item,
  parentType = "",
  isCollapsed = false,
  level = 0,
  onPress = () => {},
  hasChart = false,
  reverse = true,
}) => {
  const swipeable = useRef();
  const [lastPressed, setLastPressed] = useState(0);
  const [timer, setTimer] = useState(0);
  const [isSwipeOpen, setIsSwipeOpen] = useState(false);
  const [isSelected, setIsSelected] = useState(true);
  const [hasModifiedDays, setHasModifiedDays] = useState(false);
  const [hasChildrenChecked, setHasChildrenChecked] = useState(false);
  const numberOfLines = 1;

  // ------------------------------------- SELECTION -------------------------------------
  useEffect(() => {
    if (!isReportMode) return;

    const childrenCount = item.children ? item.children.length : 0;
    const isPartialChecked = item.isSelected
      ? item.selectedChildrenCount >= 0 &&
        item.selectedChildrenCount != childrenCount
      : item.selectedChildrenCount > 0;

    setHasChildrenChecked(isPartialChecked);
    setIsSelected(item.isSelected);

    let modifiedHours = false;
    (item.days || []).forEach((d) => {
      if (d.is_modified) modifiedHours = true;
    });
    setHasModifiedDays(modifiedHours);
  }, [item]);

  // ------------------------------------- INIT -------------------------------------

  const {
    showEditOptions = () => {},
    onDeleteElement = () => {},
    onElementSelected = () => {},
    showTutorials,
    showReflectionTutorials,
    tutorialStep,
    pageType = "",
    isDailyRoutine,
    isReportMode,
    reportStep,
    toggleElementSelection,
  } = useContext(ReflectionContext) || {};

  const { timeline } = item;
  const days =
    hasChart || isDailyRoutine || isReportMode
      ? item.days
        ? [item.days[0]]
        : []
      : item.days;

  // Name
  let itemName = item.name;
  let isGeneric = false;
  if (itemName != null && itemName.indexOf(" (Generic)") > -1) {
    itemName = itemName.replace(" (Generic)", "");
    isGeneric = true;
  }

  const isBaseSet =
    (item.item_type == "set" || isGeneric) && isBaseCoreElement(itemName);
  const ImageTag =
    item.item_id == "personal"
      ? LockIcon
      : isBaseSet
      ? getGroupImageTag(itemName)
      : ImageComponent;
  const isGoal = item.name == "Goals";
  const isInsideGoals = item.parent_id == "Goals";
  const isSetHeader = isBaseSet && !isInsideGoals;

  const rightButtons =
    (pageType == "onboarding" || pageType == "routine") &&
    item.item_type != "set"
      ? [
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => deleteElement(item)}
          >
            <Text style={styles.buttonText}>Remove {item.item_type}</Text>
          </TouchableOpacity>,
        ]
      : null;

  const onLongPressHandler = () => {
    if (showTutorials || showReflectionTutorials) return;

    if (item.item_type == "set") {
      showEditOptions(item);
    } else {
      onElementSelected(item);
    }
  };

  const onPressEventHandler = (event) => {
    if (showTutorials) return;

    if (item.item_type != "set" || isReportMode) {
      // Activities
      /* if (item.item_type == "activity") {
        showFullName();
        return;
      } */
      if (onPress) {
        onPress();
        return;
      }
    }

    event.preventDefault();
    if (timer) clearTimeout(timer);

    const now = new Date().getTime();
    setLastPressed(now);

    if (now - lastPressed <= 250) {
      if (!showTutorials && !showReflectionTutorials) {
        showEditOptions(item);
      }
    } else {
      setTimer(
        setTimeout(() => {
          onPress();
        }, 250),
      );
    }
  };

  const onOpen = () => {
    setIsSwipeOpen(true);
    if (swipeable.current) {
      swipeable.current.recenter();
    }
  };

  const onClose = () => {
    setIsSwipeOpen(false);
    //setCurrentlyOpenSwipeable(null);
  };

  const deleteElement = (element) => {
    setIsSwipeOpen(false);
    if (swipeable.current) swipeable.current.recenter();

    if (showTutorials && !(tutorialStep >= 10 || tutorialStep == 5)) {
      return;
    }

    onDeleteElement(element);
  };

  return (
    <Swipeable
      onRef={(ref) => (swipeable.current = ref)}
      rightButtonWidth={140}
      rightButtons={rightButtons}
      onRightButtonsOpenRelease={onOpen}
      onRightButtonsCloseRelease={onClose}
    >
      <View
        style={[
          styles.item,
          item.is_modified ? { backgroundColor: "#E6F9F3" } : {},
          { paddingLeft: (level - 1) * 24 || 8 },
          isReportMode && reportStep <= 3 && !isSelected
            ? { opacity: 0.4 }
            : {},
        ]}
      >
        <View
          style={{
            flex: 1,
            flexDirection: "row",
          }}
        >
          <TouchableOpacity
            onLongPress={onLongPressHandler}
            onPress={onPressEventHandler}
            style={[
              styles.resolutionTextContainer,
              item.item_type == "tag" ? styles.tagContainer : { flex: 5 },
              isInsideGoals && item.item_type == "set"
                ? styles.setGoalContainer
                : {},
              item.isSearchMatch ? styles.searchMatch : {},
            ]}
          >
            <View style={styles.logoContainer}>
              <ImageTag
                icon={item.icon}
                color={
                  isSetHeader || item.item_id == "personal"
                    ? AppTheme.almostBlack
                    : AppTheme.containerWhite
                }
                style={[
                  styles.logo,
                  isBaseSet && styles.baseLogo,
                  isGoal && styles.gemLogo,
                  parentType == "tag" && { width: 20, height: 20 },
                ]}
              />
            </View>

            <Text
              numberOfLines={numberOfLines}
              title={itemName}
              style={[
                styles.resolutionText,
                item.item_type == "set"
                  ? isInsideGoals
                    ? styles.setGoalText
                    : itemName?.length > 15 || item.item_id == "uncategorized"
                    ? styles.setTextSmall
                    : styles.setText
                  : {},
                item.item_type == "tag" ? styles.tagText : {},
                item.item_type == "activity" && styles.topActivityText,
                parentType == "tag" &&
                  item.item_type == "activity" &&
                  styles.activityInTagText,
              ]}
            >
              {itemName}
              {isGeneric ? (
                <Text style={styles.generic}> (Generic) </Text>
              ) : null}
            </Text>

            {hasItems(item.children) ? (
              <CollapseIcon
                isCollapsed={isCollapsed}
                onPress={onPressEventHandler}
              />
            ) : null}
          </TouchableOpacity>

          {hasChart && !isGoal ? (
            <ReflectionItemChart days={days} timeline={timeline} />
          ) : isReportMode && reportStep == 2 ? (
            <View style={{ flex: 1 }} />
          ) : null}
        </View>

        {hasModifiedDays ? <EditedHoursIcon /> : null}

        {/* Hours & gems */}
        {/* Hide hours for unchecked report elements */}
        {isGoal || item.item_id == "personal" ? null : (
          <View style={[styles.hours]}>
            {(reverse ? (days || []).slice(0).reverse() : days || []).map(
              (day, dayIndex) => (
                <ReflectionHours
                  isSwipeOpen={isSwipeOpen}
                  key={(days || []).length - 1 - dayIndex}
                  item={item}
                  level={level}
                  day={day}
                  dayIndex={(days || []).length - 1 - dayIndex}
                />
              ),
            )}
          </View>
        )}

        {isReportMode && reportStep == 2 ? (
          <TouchableOpacity style={styles.checkbox}>
            <RoundCheckbox
              size={16}
              checked={isSelected}
              hasChildrenChecked={hasChildrenChecked}
              onValueChange={(value) => toggleElementSelection(item, value)}
            />
          </TouchableOpacity>
        ) : null}
      </View>
    </Swipeable>
  );
};

export default ReflectionItemHeader;

const styles = StyleSheet.create({
  item: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    height: 40,
  },
  resolutionTextContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  resolutionText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: "#2D3142",
    textTransform: "capitalize",
  },
  searchMatch: {
    backgroundColor: "#F6EC91",
    borderRadius: 100,
  },
  tagContainer: {
    backgroundColor: AppTheme.lightGray,
    borderColor: AppTheme.lightGray,
    borderWidth: 1,
    borderRadius: 100,
    height: 37,
    marginLeft: 0,
    marginRight: "auto",
    maxWidth: "100%",
    paddingRight: 6,
  },
  tagText: {
    color: AppTheme.almostBlack,
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "500",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    //maxWidth: 100,
    //flex: 1,
  },
  hours: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    height: 40,
  },
  set: {},
  setText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  setTextSmall: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  topActivityText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: "#2D3142",
  },
  activityInTagText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "500",
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.175,
    color: "#2D3142",
    flex: 1,
  },

  setGoalContainer: {
    backgroundColor: "#768B9D",
    height: 37,
    minWidth: 140,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    //paddingLeft: 10,
    marginLeft: 0,
    borderRadius: 3,
  },
  setGoalText: {
    color: "#FFF",
  },
  moreWrapper: {
    paddingLeft: 20,
    marginTop: 10,
  },
  logoContainer: {
    marginRight: 6,
    position: "relative",
    width: 30,
    height: 30,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  logo: {
    width: 20,
    height: 20,
  },
  baseLogo: {
    height: 32,
    marginLeft: -10,
  },
  gemLogo: {
    width: 20,
    height: 20,
  },
  gemBg: {
    width: 30,
    height: 30,
    position: "absolute",
    left: -15,
    top: -5,
    borderWidth: 2,
    borderRadius: 30,
  },
  deleteButton: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    paddingLeft: 10,
    backgroundColor: "#F34213",
  },
  buttonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: "#FFF",
    textTransform: "capitalize",
  },
  generic: {
    fontStyle: "italic",
  },
  checkbox: {
    marginLeft: 8,
  },
});
